import { EConfirmDialogResult, useConfirmDialog } from '@/composables/useConfirmDialog';
import { PerformanceAssessmentEntryObservationStatus } from '@/core/performanceAssessments/performanceAssessmentEntryObservationStatus';
import { getObservationStatus } from '@/utils/performance.utils';
import { useI18n } from 'vue-i18n';

export type TPerformanceAssessmentCollectiveScope = 'all' | 'missing' | 'none';

interface IDescriptionValue {
  description?: string | null;
}

interface IRatingValue {
  gradeValue?: number | null;
  performanceLevelV2Id: number | null;
}

interface IPerformanceAssessmentCollectiveScopeComposable {
  getDescriptionScope: (
    values: IDescriptionValue[],
    newDescription: string,
  ) => Promise<TPerformanceAssessmentCollectiveScope>;
  getRatingScope: (ratings: IRatingValue[]) => Promise<TPerformanceAssessmentCollectiveScope>;
}

const hasDescription = (value: IDescriptionValue): boolean => !!value.description?.trim();

export const usePerformanceAssessmentCollectiveScope = (): IPerformanceAssessmentCollectiveScopeComposable => {
  const { t } = useI18n();
  const { confirmWithResult } = useConfirmDialog();

  const getRatingScope = async (ratings: IRatingValue[]): Promise<TPerformanceAssessmentCollectiveScope> => {
    if (!ratings.length) return 'none';

    const status = getObservationStatus(ratings);
    if (status === PerformanceAssessmentEntryObservationStatus.NotObserved) return 'all';

    const isPartiallyObserved = status === PerformanceAssessmentEntryObservationStatus.PartiallyObserved;
    const result = await confirmWithResult({
      title: t('performanceAssessmentEntry.confirmBulkDialog.title'),
      description: t(
        isPartiallyObserved
          ? 'performanceAssessmentEntry.confirmBulkDialog.partiallyObserved.description'
          : 'performanceAssessmentEntry.confirmBulkDialog.fullyObserved.description',
      ),
      confirmButtonLabel: t(
        isPartiallyObserved
          ? 'performanceAssessmentEntry.confirmBulkDialog.partiallyObserved.confirm'
          : 'performanceAssessmentEntry.confirmBulkDialog.fullyObserved.confirm',
      ),
      declineButtonLabel: t(
        isPartiallyObserved
          ? 'performanceAssessmentEntry.confirmBulkDialog.partiallyObserved.decline'
          : 'general.cancel',
      ),
      size: 'lg',
    });

    if (result === EConfirmDialogResult.Confirm) return 'all';
    if (result === EConfirmDialogResult.Decline && isPartiallyObserved) return 'missing';
    return 'none';
  };

  const getDescriptionScope = async (
    values: IDescriptionValue[],
    newDescription: string,
  ): Promise<TPerformanceAssessmentCollectiveScope> => {
    if (!values.length || !newDescription.trim()) return 'none';

    const valuesWithDescription = values.filter(hasDescription);
    if (!valuesWithDescription.length) return 'all';
    if (valuesWithDescription.every((value) => value.description === newDescription)) return 'all';

    const hasMissingDescriptions = valuesWithDescription.length < values.length;
    const result = await confirmWithResult({
      title: t('performanceAssessmentEntry.confirmBulkDescriptionDialog.title'),
      description: t(
        hasMissingDescriptions
          ? 'performanceAssessmentEntry.confirmBulkDescriptionDialog.partiallyDescribed.description'
          : 'performanceAssessmentEntry.confirmBulkDescriptionDialog.fullyDescribed.description',
      ),
      confirmButtonLabel: t(
        hasMissingDescriptions
          ? 'performanceAssessmentEntry.confirmBulkDescriptionDialog.partiallyDescribed.confirm'
          : 'performanceAssessmentEntry.confirmBulkDescriptionDialog.fullyDescribed.confirm',
      ),
      declineButtonLabel: t(
        hasMissingDescriptions
          ? 'performanceAssessmentEntry.confirmBulkDescriptionDialog.partiallyDescribed.decline'
          : 'general.cancel',
      ),
      size: 'lg',
    });

    if (result === EConfirmDialogResult.Confirm) return 'all';
    if (result === EConfirmDialogResult.Decline && hasMissingDescriptions) return 'missing';
    return 'none';
  };

  return { getDescriptionScope, getRatingScope };
};
